import { useState } from "react";
import type { MatrixClient } from "matrix-js-sdk";
import { ConversationList } from "./conversations/ConversationList";
import { ChatPane } from "./chat/ChatPane";
import { NewChatModal } from "./contacts/NewChatModal";
import { Text } from "../theme/primitives";

interface ChatAppProps {
  client: MatrixClient;
  accessToken: string;
}

/**
 * Top-level two-pane layout: conversation list on the left, chat on the right.
 * On narrow screens only one pane is shown at a time (list <-> chat).
 */
export function ChatApp({ client, accessToken }: ChatAppProps) {
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const isNarrow = typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches;

  const showList = !isNarrow || selectedRoomId === null;
  const showChat = !isNarrow || selectedRoomId !== null;

  return (
    <div style={{ display: "flex", height: "100%", background: "var(--color-bg)" }}>
      {showList && (
        <aside
          style={{
            width: isNarrow ? "100%" : 360,
            flexShrink: 0,
            borderRight: "1px solid var(--color-border)",
            height: "100%",
          }}
        >
          <ConversationList
            client={client}
            selectedRoomId={selectedRoomId}
            onSelect={setSelectedRoomId}
            onNewChat={() => setShowNewChat(true)}
          />
        </aside>
      )}

      {showChat && (
        <main style={{ flex: 1, display: "flex", height: "100%" }}>
          {selectedRoomId ? (
            <ChatPane
              key={selectedRoomId}
              client={client}
              roomId={selectedRoomId}
              onBack={isNarrow ? () => setSelectedRoomId(null) : undefined}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--color-bg-chat)",
              }}
            >
              <Text muted>Select a conversation to start chatting.</Text>
            </div>
          )}
        </main>
      )}

      {showNewChat && (
        <NewChatModal
          client={client}
          token={accessToken}
          onClose={() => setShowNewChat(false)}
          onStarted={(roomId) => {
            setShowNewChat(false);
            setSelectedRoomId(roomId);
          }}
        />
      )}
    </div>
  );
}
